import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgress } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import api from "../../services/api";

// Documento recebido/enviado (PDF, Word, Excel...) no estilo do WhatsApp:
// icone do tipo, nome do arquivo, tipo e as acoes Abrir / Baixar.

const TIPOS = {
  pdf: { rotulo: "PDF", cor: "#e5252a" },
  doc: { rotulo: "DOC", cor: "#2b579a" },
  docx: { rotulo: "DOCX", cor: "#2b579a" },
  xls: { rotulo: "XLS", cor: "#217346" },
  xlsx: { rotulo: "XLSX", cor: "#217346" },
  csv: { rotulo: "CSV", cor: "#217346" },
  ppt: { rotulo: "PPT", cor: "#d24726" },
  pptx: { rotulo: "PPTX", cor: "#d24726" },
  zip: { rotulo: "ZIP", cor: "#7c3aed" },
  rar: { rotulo: "RAR", cor: "#7c3aed" },
  txt: { rotulo: "TXT", cor: "#64748b" },
};

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  return {
    caixa: {
      width: 300,
      maxWidth: "100%",
      borderRadius: 8,
      overflow: "hidden",
      backgroundColor: escuro ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.045)",
      margin: "2px 0 4px",
    },
    linha: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "10px 12px",
      cursor: "pointer",
      textDecoration: "none",
      color: "inherit",
      "&:hover": { backgroundColor: escuro ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.03)" },
    },
    icone: {
      width: 34,
      height: 42,
      borderRadius: 4,
      flexShrink: 0,
      position: "relative",
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      paddingBottom: 5,
      boxSizing: "border-box",
      color: "#fff",
      fontSize: 9,
      fontWeight: 800,
      letterSpacing: ".3px",
      // dobra do papel no canto
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        right: 0,
        borderStyle: "solid",
        borderWidth: "0 10px 10px 0",
        borderColor: `transparent ${escuro ? "#1f2c34" : "#ffffff"} rgba(0,0,0,.18) transparent`,
      },
    },
    textos: { flex: 1, minWidth: 0 },
    nome: {
      fontSize: "0.88rem",
      fontWeight: 500,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    meta: { fontSize: "0.72rem", color: theme.palette.text.secondary, marginTop: 2 },
    acoes: {
      display: "flex",
      borderTop: `1px solid ${escuro ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.07)"}`,
    },
    acao: {
      flex: 1,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      padding: "8px 0",
      border: "none",
      background: "none",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: "0.8rem",
      fontWeight: 700,
      color: "#1E9855",
      textDecoration: "none",
      "&:hover": { backgroundColor: escuro ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.03)" },
      "&:focus": { outline: "none" },
      "& + &": { borderLeft: `1px solid ${escuro ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.07)"}` },
    },
  };
});

const extensaoDe = (texto) => {
  const m = String(texto || "").split("?")[0].match(/\.([a-z0-9]{2,5})$/i);
  return m ? m[1].toLowerCase() : "";
};

// Nome bonito: usa o nome original quando a mensagem traz um; os arquivos
// salvos pelo sistema ("document-1790964702811.Md4S3.pdf") viram "Documento".
const nomeDoArquivo = (corpo, url) => {
  const c = String(corpo || "").trim();
  if (c && /\.[a-z0-9]{2,5}$/i.test(c) && c.length < 140 && !/^document-\d+/i.test(c)) return c;
  const base = decodeURIComponent(String(url || "").split("/").pop() || "").split("?")[0];
  if (base && !/^document-\d+/i.test(base) && !/^\d{10,}/.test(base)) return base;
  const ext = extensaoDe(base);
  return ext ? `Documento.${ext}` : "Documento";
};

const DocumentoMensagem = ({ url, corpo }) => {
  const classes = useStyles();
  const [baixando, setBaixando] = useState(false);

  const nome = nomeDoArquivo(corpo, url);
  const ext = extensaoDe(nome) || extensaoDe(url);
  const tipo = TIPOS[ext] || { rotulo: (ext || "ARQ").toUpperCase().slice(0, 4), cor: "#64748b" };

  const abrir = () => window.open(url, "_blank", "noopener");

  const baixar = async (e) => {
    e.stopPropagation();
    if (baixando) return;
    setBaixando(true);
    try {
      const { data } = await api.get(url, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(data);
      link.download = nome;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 4000);
    } catch (err) {
      abrir(); // sem permissao de baixar direto: abre em outra aba
    } finally {
      setBaixando(false);
    }
  };

  return (
    <div className={classes.caixa}>
      <div className={classes.linha} onClick={abrir} title="Abrir documento">
        <span className={classes.icone} style={{ backgroundColor: tipo.cor }}>
          {tipo.rotulo}
        </span>
        <div className={classes.textos}>
          <div className={classes.nome} title={nome}>{nome}</div>
          <div className={classes.meta}>{tipo.rotulo} · Documento</div>
        </div>
      </div>
      <div className={classes.acoes}>
        <button type="button" className={classes.acao} onClick={abrir}>
          <OpenInNewIcon style={{ fontSize: 16 }} /> Abrir
        </button>
        <button type="button" className={classes.acao} onClick={baixar}>
          {baixando ? <CircularProgress size={14} /> : <GetAppIcon style={{ fontSize: 16 }} />} Baixar
        </button>
      </div>
    </div>
  );
};

export default DocumentoMensagem;
